import { onMounted, ref, computed, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { RootState } from '../../store';
import { getProductsAndFilters } from '../../services/productService'; // percorso del servizio
import { Product } from '../../interfaces/Product';
import FilterBar from '../FilterBar/FilterBar.vue';
import FilterModal from '../FilterModal/FilterModal.vue';
import SortByModal from '../SortByModal/SortByModal.vue';
import ProductTile from '../ProductTile/ProductTile.vue';
import SkeletonTile from '../SkeletonTile/SkeletonTile.vue';
import EspotContainer from '../EspotContainer/EspotContainer.vue';
import { FacetList, facetValue } from '@/interfaces/Filter';
import { applyFilterTrackAnalytics, onLoadTrackAnalytics } from '@/utils';
import { createSimulatedFilter, formatPriceRange } from '@/utils-helpers';

export default {
    name: 'ProductTilesContainer',
    components: {
        SkeletonTile,
        ProductTile,
        FilterBar,
        FilterModal,
        SortByModal,
        EspotContainer
    },
    beforeCreate() {
        const store = useStore();
        store.dispatch('initializeFacet');
    },
    setup() {
        const products = ref<Product[]>([]);
        const store = useStore<RootState>();
        const configurationParams = computed(() => store.state.configurationParams);
        const isFilterModalOpen = ref<boolean>(false);
        const isSortByModalOpen = ref<boolean>(false);
        const sortByModalPosition = ref({ top: 0, left: 0 });
        const sortByModalPositionClone = ref({ top: 0, left: 0, width: 0, height: 0 });
        const isLoading = ref<boolean>(true);

        const urlParams = new URLSearchParams(window.location.search);
        const currentPage = ref(Number(urlParams.get('pageNumber')) || 1);
        const pageSize = 13;
        const facetList = ref<FacetList[]>([]);
        const facetDefaultList = ref<FacetList[]>([]);
        const recordSetTotal = ref(Number(0));
        const totalFilterResult = ref(Number(0));
        const isMobile = ref(false);

        const checkDevice = () => {
            isMobile.value = window.innerWidth <= 768; // Adatta la larghezza secondo le tue necessità
        };

        const fetchProducts = async (page: number, size: number, facetString?: string, orderBy?: string, onComplete?: (recordSetTotal: number) => void) => {
            try {
                isLoading.value = true;
                if (page == 1) {
                    products.value = [];

                }
                const newProductandFilter = await getProductsAndFilters({
                    ...configurationParams.value,
                    pageNumber: page,
                    pageSize: size,
                }, facetString, orderBy);
                products.value = [...products.value, ...newProductandFilter.products];
                facetList.value = newProductandFilter.facets;
                recordSetTotal.value = newProductandFilter.recordSetTotal;
                totalFilterResult.value = newProductandFilter.recordSetTotal;
            } catch (error) {
                console.error("Error loading products: ", error);
            } finally {
                isLoading.value = false; // Fine caricamento
                if (onComplete) {
                    onComplete(recordSetTotal.value); // Chiamata della funzione quando il fetch è completato
                }
                if (store.getters.configurationParams.isSearchPage) {
                    const element = document.querySelector('.number-section');
                    if (element) {
                        element.textContent = `${recordSetTotal.value} results`;
                    }
                }
            }
        };

        const loadInitialProducts = async () => {
            try {
                const orderBy = new URLSearchParams(window.location.search).get('orderBy') || '';
                const facetFromUrl = findMatchingFacets().join('&facet=');
                if (currentPage.value > 1) {
                    // Effettua una prima chiamata per ottenere il totale dei record
                    await fetchProducts(1, pageSize, facetFromUrl, orderBy);
                    await fetchProducts(2, pageSize, facetFromUrl, orderBy);

                    // Calcola il numero massimo di pagine in base a recordSetTotal
                    const maxPage = Math.ceil(recordSetTotal.value / pageSize);

                    // Se currentPage.value è maggiore del massimo possibile, correggilo
                    if (currentPage.value > maxPage) {
                        console.warn(`Page ${currentPage.value} not valid. Set to ${maxPage}.`);
                        currentPage.value = maxPage;
                        urlParams.set('pageNumber', (currentPage.value).toString());
                        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
                        window.history.pushState(null, '', newUrl);
                    }

                    // Calcola da dove iniziare il recupero dei prodotti
                    const startPage = Math.floor((pageSize * 2) / pageSize) + 1; // Da dove partire dopo la prima chiamata
                    const remainingPages = currentPage.value - startPage;

                    // Recupera i prodotti per le pagine successive, se necessario
                    for (let page = startPage; page <= currentPage.value + 1; page++) {
                        await fetchProducts(page, pageSize, facetFromUrl, orderBy, () => {
                            if (page === currentPage.value + 1) {
                                // Chiamata all'ultimo giro del ciclo
                                onLoadTrackAnalytics(transformProductsForAnalytics(products.value)); // Chiama la tua funzione
                            }
                        });
                    }
                } else {
                    await fetchProducts(1, pageSize, facetFromUrl, orderBy);
                    await fetchProducts(2, pageSize, facetFromUrl, orderBy, () => {
                        // Chiamata completata, esegui la tua funzione
                        onLoadTrackAnalytics(transformProductsForAnalytics(products.value));
                    })
                }
            } catch (error) {
                console.error("Error in initial product loading: ", error);
            }
        };

        const transformProductsForAnalytics = (products: Product[]) => {
            const result: Record<string, {
                Status: string;
                Category: string;
                Sku: string;
                Price: string;
                PriceFull: string;
            }> = {};

            products.forEach(product => {
                const productType = product.productAttributes.get("PRODUCT_TYPE");
                const electronicCheck = product.productAttributes.get("PROD_HIERARCHY_1") || "";
                const isEletronicsCategory = electronicCheck.includes("Electronics") ? true : false;
                let category = "";


                // Determina la categoria in base al PRODUCT_TYPE
                if (productType) {
                    if (isEletronicsCategory) {
                        category = "Electronics";
                    } else if (productType.includes("Optical")) {
                        category = "OPTICS"; // Se contiene "Optical"
                    } else if (productType.includes("Sun")) {
                        category = "SUN"; // Se contiene "Sun"
                    } else {
                        // Altrimenti prendi la parte dopo il "_"
                        category = productType.split("_")[1] || "Other";
                    }
                }

                // Aggiunge il prodotto all'oggetto result usando il partNumber come chiave
                result[product.partNumber] = {
                    Status: product.buyable ? "Available" : "Out-of-stock",
                    Category: category || "", // Categoria 
                    Sku: product.productAttributes.get("MODEL_NAME") || "", // MODEL_NAME come SKU
                    Price: product.offerPrice || "0.00",
                    PriceFull: product.listPrice || "0.00"
                };
            });

            return result;
        };

        const filterurl = () => {
            const url = new URL(window.location.href);
            url.searchParams.set('pageNumber', (currentPage.value + 1).toString());
            return url.toString();
        };

        const hiddenurl = () => {
            const url = new URL(window.location.href);
            url.searchParams.set('pageNumber', (currentPage.value).toString())
            return url.toString();
        };

        const showhiddenurl = () => {
            if (currentPage.value == 1) {
                return false;
            } else {
                return true;
            }
        };

        const loadMoreProducts = async () => {
            if (isLoading.value) return; // Prevenire clic multipli        
            try {
                isLoading.value = true; // Mostra le SkeletonTile
                currentPage.value += 1;
                // Recupera i facet dall'URL
                const facetParams = getFacetParamsFromUrl();
                const orderBy = new URLSearchParams(window.location.search).get('orderBy') || '';
                const concatenatedFacets = facetParams.join('&facet=');
                // Effettua la chiamata per caricare più prodotti
                await fetchProducts(currentPage.value + 1, pageSize, concatenatedFacets, orderBy);
                // Aggiorna l'URL con il nuovo valore di `pageNumber`
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.set('pageNumber', currentPage.value.toString());
                const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
                window.history.pushState(null, '', newUrl);
            } catch (error) {
                console.error("Error loading products: ", error);
            } finally {
                isLoading.value = false; // Nascondi le SkeletonTile
            }
        };

        const handlePopState = async () => {
            try {
                store.commit('RESET_SELECTED_FILTERS');
                const params = new URLSearchParams(window.location.search);
                const pageNumber = Number(params.get('pageNumber')) || 1;
                currentPage.value = pageNumber;
                products.value = [];
                loadInitialProducts();
            } catch (error) {
                console.error("Error in handling popstate: ", error);
            }
        };

        const isLargeTile = (index: any) => {
            const positionInGroup = index % pageSize;
            return positionInGroup === 5 || positionInGroup === 12;
        };

        const isLargeTileMobile = (index: any) => {
            const positionInGroup = index % pageSize;
            return positionInGroup === 4 || positionInGroup === 9 || positionInGroup === 12;
        };

        const openFilterModal = (): void => {
            const contentWrapper = document.getElementById('content_wrapper_box');
            if (contentWrapper) {
                contentWrapper.classList.add('overlay-top');
            }
            isFilterModalOpen.value = true;
            document.documentElement.classList.add('noScroll');
            document.body.classList.add('noScroll');
        };

        const closeFilterModal = (): void => {
            const contentWrapper = document.getElementById('content_wrapper_box');
            if (contentWrapper) {
                contentWrapper.classList.remove('overlay-top');
            }
            isFilterModalOpen.value = false;
            document.documentElement.classList.remove('noScroll');
            document.body.classList.remove('noScroll');
        };

        const applyFilters = async () => {
            // Chiudi il modal dei filtri
            closeFilterModal();
            window.scrollTo(0, 0);

            // Gestisci l'applicazione dei filtri
            const filterLength = store.getters.selectedFilters.length;

            // Crea un oggetto URLSearchParams per gestire i parametri della query
            const urlParams = new URLSearchParams(window.location.search);

            // Azzera tutti i parametri 'facet' esistenti nell'URL
            urlParams.delete('facet');

            if (filterLength > 0) {
                // Aggiungi ogni filtro come parametro separato
                store.state.selectedFilters.forEach((filter: facetValue) => {
                    urlParams.append('facet', filter.value); // Aggiungi ogni valore 'facet' separatamente
                    if (filter.attributeLabel === 'Price') {
                        const numbers = filter.label.match(/(\d+(?:\.\d+)?)/g)?.map(parseFloat);

                        if (!numbers || numbers.length === 0) return; // Evita errori se non ci sono numeri

                        let min = store.getters.minPriceFilter, max = store.getters.maxPriceFilter;

                        if (filter.label.toLowerCase().includes("under")) {
                            max = numbers[0];
                        } else if (filter.label.toLowerCase().includes("and up")) {
                            min = numbers[0];
                            max = Infinity; // Usa Infinity invece di un valore arbitrario
                        } else if (numbers.length === 2) {
                            [min, max] = numbers;
                        }

                        // Assicurati che min e max siano validi prima di aggiornare lo stato
                        if (!isNaN(min) && !isNaN(max)) {
                            store.dispatch('updateSelectedPriceRange', [min, max]);
                        }
                    }
                });
            }

            // Recupera il parametro di ordinamento dall'URL
            const orderBy = urlParams.get('orderBy') || '';

            // Reset pageNumber
            currentPage.value = 1;
            urlParams.delete('pageNumber');

            // Crea la nuova URL
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`;

            // Aggiorna la URL senza ricaricare la pagina
            window.history.pushState(null, '', newUrl);

            // Effettua la chiamata per i prodotti aggiornati
            const concatenatedValues = urlParams.getAll('facet').join('&facet=');
            await fetchProducts(1, pageSize, concatenatedValues, orderBy);
            await fetchProducts(2, pageSize, concatenatedValues, orderBy, (recordSetTotal) => {
                applyFilterTrackAnalytics(formatFilterForAnalytics(store.state.selectedFilters, orderBy), recordSetTotal);
            });

        };

        function formatFilterForAnalytics(filterApplied: facetValue[], orderBy?: string) {
            const filtersFormatted = filterApplied.map(filter => {
                let labelValue = "";
                let attributeLabel = "";

                if (filter.label?.toLowerCase() === "high prescription") {
                    labelValue = "true";
                    attributeLabel = "High Prescription";
                } else if (filter.attributeLabel?.toLowerCase() === "virtual mirror") {
                    labelValue = "true";
                    attributeLabel = "Vto";
                } else if (filter.attributeLabel?.toLowerCase() === "gender") {
                    if (filter.label === "Children") {
                        labelValue = "true";
                        attributeLabel = "Kids";
                    } else {
                        labelValue = filter.label;
                        attributeLabel = filter.attributeLabel || "";
                    }
                } else if (filter.attributeLabel?.toLowerCase() === "price") {
                    labelValue = filter.label
                        .replace(/Under/gi, "0-")
                        .replace(/[^0-9-.]/g, "")
                        .replace(/\.00/, "");

                    if (/up/i.test(filter.label)) {
                        labelValue = "Over" + labelValue;
                    }

                    attributeLabel = 'Price';
                } else if (filter.attributeLabel?.toLowerCase().includes("promo")) {
                    labelValue = filter.label.replace("%", " Off").replace(/-/g, "");
                    attributeLabel = "Promo";
                } else {
                    labelValue = filter.label;
                    attributeLabel = filter.attributeLabel || "";
                }

                return `${attributeLabel.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join("")}=${labelValue.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join("")}`;
            });

            let sortByValue = "";
            if (orderBy && orderBy.trim() !== "") {
                switch (orderBy) {
                    case "11":
                    case "4":
                        sortByValue = "SortBy=PriceHighToLow";
                        break;
                    case "10":
                    case "3":
                        sortByValue = "SortBy=PriceLowToHigh";
                        break;
                    case "5":
                        sortByValue = "SortBy=BestSeller";
                        break;
                    case "17":
                        sortByValue = "SortBy=Newest";
                        break;
                }
            }

            if (sortByValue) {
                filtersFormatted.push(sortByValue);
            }

            return filtersFormatted.join('|');
        }

        const resetFilters = async () => {
            store.commit('RESET_SELECTED_FILTERS');
            applyFilters();
        }

        const applySort = async (orderBy: any) => {
            try {
                // Azzera i filtri applicati
                store.commit("RESET_SELECTED_FILTERS");
                facetList.value = [];
                currentPage.value = 1;

                // Aggiorna l'URL rimuovendo i filtri e aggiungendo il parametro `orderBy`
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.delete("facet"); // Rimuove tutti i filtri
                urlParams.delete("pageNumber"); // Resetta il numero di pagina
                urlParams.delete("orderBy");
                if (orderBy != '') {
                    urlParams.set("orderBy", orderBy); // Aggiunge il parametro di ordinamento
                }


                const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
                window.history.pushState(null, "", newUrl);

                // Rifai la chiamata con il nuovo ordine
                window.scrollTo(0, 0);
                await fetchProducts(1, pageSize, '', orderBy);
                //await fetchProducts(2, pageSize, '', orderBy);
                await fetchProducts(2, pageSize, '', orderBy, (recordSetTotal) => {
                    applyFilterTrackAnalytics(formatFilterForAnalytics(store.state.selectedFilters, orderBy), recordSetTotal);
                });
            } catch (error) {
                console.error("Error applying sort: ", error);
            }
        };

        const openSortByModal = (): void => {
            document.documentElement.classList.add('noScroll');

            requestAnimationFrame(() => {
                const sortByExternalbutton = document.getElementById("sort-by-button");
                if (sortByExternalbutton) {
                    const rect = sortByExternalbutton.getBoundingClientRect();
                    sortByModalPosition.value = {
                        top: rect.bottom,
                        left: rect.left,
                    };
                    sortByModalPositionClone.value = {
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                    };
                }
                isSortByModalOpen.value = true;
            });
        };


        const closeSortByModal = (): void => {
            isSortByModalOpen.value = false;
            document.documentElement.classList.remove('noScroll');
        };

        // Usa il watch per osservare i cambiamenti dei filtri e rigenerare la componente
        watch(() => store.state.selectedFilters, async (newFilters) => {

            // Quando i filtri cambiano, puoi aggiornare il componente o fare altre azioni
            console.log('Filters are changed ', newFilters);
            // Logica per rigenerare o aggiornare la UI
            const concatenatedValues = newFilters
                .map((filter: facetValue) => filter.value)  // Aggiungi il tipo esplicitamente
                .join('&facet=');
            console.log("url", concatenatedValues)
            try {
                const filterUpadated = await getProductsAndFilters({
                    ...configurationParams.value,
                    pageNumber: 1,
                    pageSize: 1,

                }, concatenatedValues);

                if (facetList.value.length > 0 && filterUpadated.facets.length > 0) {

                    filterUpadated.facets.forEach((element, i) => {

                        facetList.value[i].entry = [...element.entry];

                    });

                }

                totalFilterResult.value = filterUpadated.recordSetTotal;

            } catch (error) {
                console.error("Error loading products: ", error);
            }
        });

        onMounted(() => {
            window.addEventListener('popstate', handlePopState);

            let isFacetLoaded = false;
            let isCategoriesMapLoaded = false;

            watch(() => store.getters.defaultFacet, (newValue) => {
                if (newValue !== null) {
                    isFacetLoaded = true;
                    if (isCategoriesMapLoaded) {
                        loadInitialProducts();
                    }
                } else {
                    isFacetLoaded = false;
                }
            }, { immediate: true });

            watch(() => store.getters.categoriesMap, (newValue) => {
                if (newValue && newValue.size > 0) {
                    isCategoriesMapLoaded = true;
                    if (isFacetLoaded) {
                        loadInitialProducts();
                    }
                } else {
                    isCategoriesMapLoaded = false;
                }
            }, { immediate: true });
            checkDevice();
            window.addEventListener("resize", checkDevice);
        });



        onUnmounted(() => {
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener("resize", checkDevice);
        });

        function getFacetParamsFromUrl(): string[] {
            const urlParams = new URLSearchParams(window.location.search); // Ottieni i parametri dalla query string
            const facetParams: string[] = urlParams.getAll('facet'); // Recupera tutti i valori del parametro 'facet'
            return facetParams;
        }

        function findMatchingFacets(): any[] {
            const encodedFacets = getFacetParamsFromUrl();
            const decodedFacets = encodedFacets.map(facet => decodeURIComponent(facet)); // Decodifica i valori del primo array

            decodedFacets.map(facet => {
                const [facetKey, facetValue] = facet.split(':');

                const facetGroup = store.getters.defaultFacet.find((group: { value: string; }) => group.value === facetKey);
                if (!facetGroup) return null;



                let entryMatch = facetGroup.entry.find((entry: { value: string; }) => decodeURIComponent(entry.value) === facet) as facetValue | undefined;


                if (facet.includes("price")) {
                    let priceRange = extractPriceRange(facet);
                    if (!priceRange) {
                        console.error("Errore: priceRange è null");
                        priceRange = [0, 1000];
                    }
                    store.dispatch('updateSelectedPriceRange', priceRange);
                    if (!entryMatch) {
                        entryMatch = createSimulatedFilter(facet, priceRange, true);
                    }
                }



                store.dispatch('toggleFilter', entryMatch);
            });

            return encodedFacets
        }


        function extractPriceRange(filterValue: string): [number, number] | null {
            // Espressione regolare per catturare i valori minimi e massimi
            const match = filterValue.match(/\(\{(\d+|\*)\+(\*|\d+\.?\d*)\}(?:\+\d+\.?\d*)?\)/);

            if (match) {
                // Gestisce il caso in cui il valore minimo è * (considerato 0)
                const min = match[1] === '*' ? store.getters.minPriceFilter : parseInt(match[1], 10);

                // Gestisce il caso in cui il massimo è +* (considerato infinito) o un numero
                const max = match[2] === '*' ? store.getters.maxPriceFilter : parseFloat(match[2]);

                return [min, max];
            }

            return null; // Se non trova corrispondenza, restituisce null
        }




        return {
            pageSize,
            products,
            isLoading,
            isLargeTile,
            isLargeTileMobile,
            filterurl,
            showhiddenurl,
            hiddenurl,
            loadMoreProducts,
            openFilterModal,
            closeFilterModal,
            openSortByModal,
            closeSortByModal,
            sortByModalPosition,
            sortByModalPositionClone,
            isSortByModalOpen,
            isFilterModalOpen,
            applyFilters,
            facetList,
            totalFilterResult,
            recordSetTotal,
            resetFilters,
            applySort,
            isMobile
        };
    },
};